import type { ReactNode } from 'react';
import { cx } from '../cx';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { LABEL_TEXT, TICK_TEXT, VALUE_TEXT, fixedScale, niceScale, percentOf, thinTick } from './scale';

export type DumbbellRow = {
  id: string;
  label: ReactNode;
  /** The earlier or comparison value (open ring). */
  a: number;
  /** The newer or subject value (sun dot, labelled). */
  b: number;
  /** `b`, formatted. */
  display: ReactNode;
};

export type DumbbellProps = {
  rows: DumbbellRow[];
  /** The scale; defaults to round numbers around every value. */
  scale?: { min: number; max: number };
  formatTick: (value: number) => string;
  /** `auto` (default) switches to the narrow form in a narrow container: each label above its row, the plot full width. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: '@md:w-1/4 @md:shrink-0',
  narrow: '',
  wide: 'w-1/4 shrink-0',
};
// In a column, flex-1 would collapse the plot to no height: the narrow form sizes it by width.
const PLOT: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };

/** Two values per category joined by a line: then and now, this city and Finland. */
export function Dumbbell({ rows, scale: fixed, formatTick, layout = 'auto', description, className }: DumbbellProps) {
  const values = rows.flatMap((r) => [r.a, r.b]);
  const scale = fixed ? fixedScale(fixed.min, fixed.max) : niceScale(Math.min(...values), Math.max(...values));
  const pos = (v: number) => percentOf(v, scale.min, scale.max);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      <div className="flex gap-3" aria-hidden="true">
        <span className={cx('w-1/4 shrink-0', WIDE_ONLY[layout])} />
        <div className="relative mx-12 h-4 flex-1">
          {scale.ticks.map((t, i) => (
            <span
              key={t}
              className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT, thinTick(i, scale.ticks.length))}
              style={{ left: `${pos(t)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
      </div>
      {rows.map((r) => {
        const lo = Math.min(pos(r.a), pos(r.b));
        const hi = Math.max(pos(r.a), pos(r.b));
        const rising = r.b >= r.a;
        return (
          <div key={r.id} className={ROW[layout]}>
            <span className={cx('truncate', LABEL_TEXT, ROW_LABEL[layout])}>{r.label}</span>
            <div className={cx('relative mx-12 h-6', PLOT[layout])}>
              <span
                className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-jc-chart-context"
                style={{ left: `${lo}%`, width: `${hi - lo}%` }}
              />
              <span
                className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-jc-surface ring-2 ring-jc-ink-secondary"
                style={{ left: `${pos(r.a)}%` }}
              />
              <span
                className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-jc-chart-2 ring-2 ring-jc-ink"
                style={{ left: `${pos(r.b)}%` }}
              />
              <span
                className={cx(
                  'absolute top-1/2 -translate-y-1/2',
                  VALUE_TEXT,
                  rising ? 'pl-3' : '-translate-x-full pr-3',
                )}
                style={{ left: `${pos(r.b)}%` }}
              >
                {r.display}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The legend for a Dumbbell: what the ring and the dot are, translated. */
export function DumbbellLegend({ a, b }: { a: ReactNode; b: ReactNode }) {
  return (
    <>
      <span className="inline-flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-jc-surface ring-2 ring-jc-ink-secondary" />
        {a}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-jc-chart-2 ring-2 ring-jc-ink" />
        {b}
      </span>
    </>
  );
}
